import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { spLogTags } from "@eventr/common";
import { compareVersions, validate } from "compare-versions";

/**
 * Refuses a bundle that is not strictly newer than what is already running.
 *
 * `getLatest()` returns whatever the update server offers for this build's customId, and
 * the customId is just `VITE_APP_VERSION`. Neither the plugin nor the caller compared
 * versions before this, so an older bundle on the channel was downloaded and `set()`
 * without complaint - a silent downgrade.
 *
 * Compared against the running OTA bundle when there is one, and against the built-in web
 * build's version otherwise: a fresh install reports its bundle version as "builtin",
 * which is not a semver and cannot be ordered.
 *
 * It only ever blocks on a *positive* answer - we know what is running, and the offer is
 * not newer. When the running version cannot be determined it allows the update, which is
 * exactly today's behaviour. That asymmetry is deliberate: neither ci-ios-test-build.sh
 * nor ci-android-test-build.sh sets VITE_APP_VERSION, so on a CI build a fresh install has
 * no version to compare against, and failing closed there would silently disable OTA
 * updates for every user on a fresh install - a far worse outcome than the downgrade this
 * guards against.
 *
 * Deliberately NOT applied to `switchReactBuild()` - switching stage is a request for a
 * *different* bundle, which is legitimately older or on another version line.
 *
 * Note this does not stop a local build (`0.0.0.0-local`) from being replaced by a
 * released bundle: that genuinely is newer, so the guard allows it. A local build never
 * asks, as `otaUpdatesEnabled` is off outside the pipeline.
 */
export const isNewerBundle = async (latestVersion: string | undefined): Promise<boolean> => {
  if (!latestVersion || !validate(latestVersion)) {
    spLogTags("Skipping update, unusable version from server", ["UPDATE"], String(latestVersion));
    return false;
  }

  let running: string | undefined = import.meta.env.VITE_APP_VERSION;
  try {
    const current = await CapacitorUpdater.current();
    if (current.bundle?.version && validate(current.bundle.version)) running = current.bundle.version;
  } catch (error) {
    spLogTags("Could not read the running bundle, falling back to the build version", ["UPDATE"], error);
  }

  if (!running || !validate(running)) {
    spLogTags("Allowing update, the running version is unknown", ["UPDATE"], String(running));
    return true;
  }

  if (compareVersions(latestVersion, running) !== 1) {
    spLogTags(`Skipping update, ${latestVersion} is not newer than ${running}`, ["UPDATE"]);
    return false;
  }
  return true;
};
